import apiService from './api';

// Interface pour les rapports prédéfinis
export interface PredefinedReport {
  id: string;
  name: string;
  description: string;
  category: string;
  data: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface StatisticsData {
  total_pesées: number;
  pesées_en_cours: number;
  pesées_complétées: number;
  pesées_refoulées: number;
  pesées_en_attente: number;
  poids_total_brut: number;
  poids_total_net: number;
  poids_moyen_brut: number;
  poids_moyen_net: number;
}

// Interface pour la réponse du backend (noms différents)
interface BackendStatisticsData {
  total_pesees: number;
  pesees_en_cours: number;
  pesees_completees: number;
  pesees_refoulees: number;
  pesees_en_attente: number;
  poids_total_brut: number;
  poids_total_net: number;
  poids_moyen_brut: number;
  poids_moyen_net: number;
}

export interface TimeSeriesData {
  date: string;
  total: number;
  completed: number;
  rejected: number;
  in_progress: number;
  weight_total: number;
}

export interface ProductStatistics {
  produit_nom: string;
  produit_type: string;
  total_pesées: number;
  poids_total: number;
  poids_moyen: number;
  taux_complétion: number;
}

export interface ClientStatistics {
  client_nom: string;
  total_pesées: number;
  poids_total: number;
  poids_moyen: number;
  taux_complétion: number;
}

export interface DriverStatistics {
  chauffeur_nom: string;
  chauffeur_prénom: string;
  total_pesées: number;
  poids_total: number;
  poids_moyen: number;
  taux_complétion: number;
}

export interface VehicleStatistics {
  camion_immatriculation: string;
  camion_marque: string;
  camion_modèle: string;
  total_pesées: number;
  poids_total: number;
  poids_moyen: number;
  taux_complétion: number;
}

export interface SupplierStatistics {
  fournisseur_nom: string;
  total_pesées: number;
  poids_total: number;
  poids_moyen: number;
  taux_complétion: number;
}

export interface TransporterStatistics {
  transporteur_nom: string;
  total_pesées: number;
  poids_total: number;
  poids_moyen: number;
  taux_complétion: number;
}

export interface ExporterStatistics {
  exportateur_nom: string;
  total_pesées: number;
  poids_total: number;
  poids_moyen: number;
  taux_complétion: number;
}

export interface ReportData {
  title: string;
  description: string;
  data_type: string;
  filters: Record<string, any>;
  chart_type: string;
  data: any[];
}

export interface StatisticsFilters {
  start_date?: string;
  end_date?: string;
  period?: 'day' | 'week' | 'month' | 'year';
  product_id?: number | string;
  client_id?: number | string;
  fournisseur_id?: number | string;
  magasin_id?: number | string;
  transporteur_id?: number | string;
  exportateur_id?: number | string;
  type_pesee?: string;
  status?: string;
}

class StatisticsService {
  private extractData<T>(response: any): { success: boolean; data: T; message: string } {
    return response.data;
  }

  private appendCommonFilters(params: URLSearchParams, filters?: StatisticsFilters) {
    if (!filters) return;
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.period) params.append('period', filters.period);
    if (filters.product_id) params.append('product_id', String(filters.product_id));
    if (filters.client_id) params.append('client_id', String(filters.client_id));
    if (filters.fournisseur_id) params.append('fournisseur_id', String(filters.fournisseur_id));
    if (filters.magasin_id) params.append('magasin_id', String(filters.magasin_id));
    if (filters.transporteur_id) params.append('transporteur_id', String(filters.transporteur_id));
    if (filters.exportateur_id) params.append('exportateur_id', String(filters.exportateur_id));
    if (filters.type_pesee) params.append('type_pesee', filters.type_pesee);
    if (filters.status) params.append('status', filters.status);
  }

  /**
   * Convertit les données du backend vers le format frontend
   */
  private convertBackendStatisticsData(backendData: BackendStatisticsData): StatisticsData {
    return {
      total_pesées: backendData.total_pesees,
      pesées_en_cours: backendData.pesees_en_cours,
      pesées_complétées: backendData.pesees_completees,
      pesées_refoulées: backendData.pesees_refoulees,
      pesées_en_attente: backendData.pesees_en_attente,
      poids_total_brut: backendData.poids_total_brut,
      poids_total_net: backendData.poids_total_net,
      poids_moyen_brut: backendData.poids_moyen_brut,
      poids_moyen_net: backendData.poids_moyen_net
    };
  }

  /**
   * Génère des données de démonstration pour les séries temporelles
   */
  private generateMockTimeSeriesData(filters?: StatisticsFilters): TimeSeriesData[] {
    const period = filters?.period || 'month';
    const days = period === 'day' ? 1 : period === 'week' ? 7 : period === 'month' ? 30 : 365;
    
    const mockData: TimeSeriesData[] = [];
    const today = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      // Générer des données aléatoires réalistes
      const baseTotal = Math.floor(Math.random() * 20) + 5;
      const completed = Math.floor(baseTotal * (0.7 + Math.random() * 0.2));
      const rejected = Math.floor(baseTotal * (0.05 + Math.random() * 0.1));
      const inProgress = baseTotal - completed - rejected;
      
      mockData.push({
        date: date.toISOString().split('T')[0],
        total: baseTotal,
        completed: completed,
        rejected: rejected,
        in_progress: inProgress,
        weight_total: Math.floor(Math.random() * 10000) + 1000
      });
    }
    
    return mockData;
  }

  /**
   * Récupère les statistiques générales des pesées
   */
  async getGeneralStats(filters?: StatisticsFilters): Promise<StatisticsData> {
    try {
      const params = new URLSearchParams();
      this.appendCommonFilters(params, filters);

      const response = await apiService.get('/weighing/api/statistics/general_stats/', { params });
      const result = this.extractData<BackendStatisticsData>(response);
      
      if (!result.success) {
        throw new Error(result.message);
      }
      
      // Convertir les données du backend vers le format frontend
      return this.convertBackendStatisticsData(result.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques générales:', error);
      // Retourner des données de démonstration en cas d'erreur
      return {
        total_pesées: 0,
        pesées_en_cours: 0,
        pesées_complétées: 0,
        pesées_refoulées: 0,
        pesées_en_attente: 0,
        poids_total_brut: 0,
        poids_total_net: 0,
        poids_moyen_brut: 0,
        poids_moyen_net: 0
      };
    }
  }

  /**
   * Récupère les données de série temporelle pour les graphiques
   */
  async getTimeSeriesData(filters?: StatisticsFilters): Promise<TimeSeriesData[]> {
    try {
      const params = new URLSearchParams();
      this.appendCommonFilters(params, filters);
      params.append('filter_empty', 'true'); // Filtrer les dates vides

      const response = await apiService.get('/weighing/api/statistics/time_series/', { params });
      const result = this.extractData<TimeSeriesData[]>(response);
      
      if (!result.success) {
        throw new Error(result.message);
      }
      
      return result.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des données temporelles:', error);
      // Retourner des données de démonstration en cas d'erreur
      return this.generateMockTimeSeriesData(filters);
    }
  }

  /**
   * Récupère les statistiques par produit
   */
  async getProductStats(filters?: StatisticsFilters): Promise<ProductStatistics[]> {
    try {
      const params = new URLSearchParams();
      this.appendCommonFilters(params, filters);

      const response = await apiService.get('/weighing/api/statistics/product_stats/', { params });
      const result = this.extractData<ProductStatistics[]>(response);
      
      if (!result.success) {
        throw new Error(result.message);
      }
      
      return result.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques par produit:', error);
      throw error;
    }
  }

  /**
   * Récupère les statistiques par client
   */
  async getClientStats(filters?: StatisticsFilters): Promise<ClientStatistics[]> {
    try {
      const params = new URLSearchParams();
      this.appendCommonFilters(params, filters);

      const response = await apiService.get('/weighing/api/statistics/client_stats/', { params });
      const result = this.extractData<ClientStatistics[]>(response);
      
      if (!result.success) {
        throw new Error(result.message);
      }
      
      return result.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques par client:', error);
      throw error;
    }
  }

  /**
   * Récupère les statistiques par chauffeur
   */
  async getDriverStats(filters?: StatisticsFilters): Promise<DriverStatistics[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.start_date) params.append('start_date', filters.start_date);
      if (filters?.end_date) params.append('end_date', filters.end_date);

      const response = await apiService.get('/weighing/api/statistics/driver_stats/', { params });
      const result = this.extractData<DriverStatistics[]>(response);
      
      if (!result.success) {
        throw new Error(result.message);
      }
      
      return result.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques par chauffeur:', error);
      throw error;
    }
  }

  /**
   * Récupère les statistiques par véhicule
   */
  async getVehicleStats(filters?: StatisticsFilters): Promise<VehicleStatistics[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.start_date) params.append('start_date', filters.start_date);
      if (filters?.end_date) params.append('end_date', filters.end_date);

      const response = await apiService.get('/weighing/api/statistics/vehicle_stats/', { params });
      const result = this.extractData<VehicleStatistics[]>(response);
      
      if (!result.success) {
        throw new Error(result.message);
      }
      
      return result.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques par véhicule:', error);
      throw error;
    }
  }

  /**
   * Récupère les statistiques par fournisseur
   */
  async getSupplierStats(filters?: StatisticsFilters): Promise<SupplierStatistics[]> {
    try {
      const params = new URLSearchParams();
      this.appendCommonFilters(params, filters);

      const response = await apiService.get('/weighing/api/statistics/supplier_stats/', { params });
      const result = this.extractData<SupplierStatistics[]>(response);
      
      if (!result.success) {
        throw new Error(result.message);
      }
      
      return result.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques par fournisseur:', error);
      throw error;
    }
  }

  /**
   * Récupère les statistiques par transporteur
   */
  async getTransporterStats(filters?: StatisticsFilters): Promise<TransporterStatistics[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.start_date) params.append('start_date', filters.start_date);
      if (filters?.end_date) params.append('end_date', filters.end_date);

      const response = await apiService.get('/weighing/api/statistics/transporter_stats/', { params });
      const result = this.extractData<TransporterStatistics[]>(response);
      
      if (!result.success) {
        throw new Error(result.message);
      }
      
      return result.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques par transporteur:', error);
      throw error;
    }
  }

  /**
   * Récupère les statistiques par exportateur
   */
  async getExporterStats(filters?: StatisticsFilters): Promise<ExporterStatistics[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.start_date) params.append('start_date', filters.start_date);
      if (filters?.end_date) params.append('end_date', filters.end_date);

      const response = await apiService.get('/weighing/api/statistics/exporter_stats/', { params });
      const result = this.extractData<ExporterStatistics[]>(response);
      
      if (!result.success) {
        throw new Error(result.message);
      }
      
      return result.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques par exportateur:', error);
      throw error;
    }
  }

  /**
   * Génère un rapport personnalisé
   */
  async generateCustomReport(reportConfig: {
    title: string;
    description: string;
    data_type: string;
    filters: Record<string, any>;
    chart_type: string;
  }): Promise<ReportData> {
    try {
      const response = await apiService.post('/weighing/api/statistics/custom_report/', reportConfig);
      const result = this.extractData<ReportData>(response);
      
      if (!result.success) {
        throw new Error(result.message);
      }
      
      return result.data;
    } catch (error) {
      console.error('Erreur lors de la génération du rapport personnalisé:', error);
      throw error;
    }
  }

  /**
   * Récupère la liste des rapports prédéfinis
   */
  async getPredefinedReports(): Promise<PredefinedReport[]> {
    try {
      const response = await apiService.get('/weighing/api/statistics/predefined_reports/');
      const result = this.extractData<PredefinedReport[]>(response);
      
      if (!result.success) {
        throw new Error(result.message);
      }
      
      return result.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des rapports prédéfinis:', error);
      throw error;
    }
  }


  /**
   * Génère un rapport prédéfini
   */
  async generatePredefinedReport(reportId: string, reportData?: any, filters?: StatisticsFilters): Promise<ReportData> {
    try {
      // Déterminer le type de données et la période
      let dataType = 'general';
      let period: 'day' | 'week' | 'month' | 'year' = 'day';
      
      if (reportData && reportData.data) {
        dataType = reportData.data.type || 'general';
        period = (reportData.data.period as 'day' | 'week' | 'month' | 'year') || 'day';
      } else {
        // Fallback basé sur l'ID
        if (reportId.includes('product')) dataType = 'product';
        else if (reportId.includes('client')) dataType = 'client';
        else if (reportId.includes('quality_kor')) dataType = 'quality_kor';
        else if (reportId.includes('humidity')) dataType = 'humidity';
      }

      // Calculer les dates selon la période (seulement pour les rapports généraux)
      let reportFilters = { ...filters };
      
      if (dataType === 'general' && period) {
        const now = new Date();
        let startDate: Date;
        let endDate: Date = new Date(now);

        switch (period) {
          case 'day':
            startDate = new Date(now);
            startDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            startDate = new Date(now);
            startDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            startDate = new Date(now);
            startDate.setDate(1); // Premier jour du mois
            break;
          default:
            startDate = new Date(now);
            startDate.setDate(now.getDate() - 30); // 30 jours par défaut
        }

        reportFilters = {
          ...filters,
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
          period: period
        };
      }

      const reportConfig = {
        title: `Rapport ${reportId}`,
        description: `Rapport prédéfini ${reportId}`,
        data_type: dataType,
        filters: reportFilters,
        chart_type: 'bar'
      };

      return await this.generateCustomReport(reportConfig);
    } catch (error) {
      console.error('Erreur lors de la génération du rapport prédéfini:', error);
      throw error;
    }
  }

  /**
   * Exporte un rapport dans le format spécifié
   */
  async exportReport(reportData: any, format: 'pdf' | 'excel' | 'csv'): Promise<void> {
    try {
      const response = await apiService.post('/weighing/api/statistics/export_report/', {
        format: format,
        report_data: reportData
      }, {
        responseType: 'blob' // Important pour les fichiers binaires
      });

      // Créer un blob et télécharger le fichier
      const blob = new Blob([response.data], {
        type: format === 'pdf' ? 'application/pdf' :
              format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' :
              'text/csv'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Générer un nom de fichier
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const cleanTitle = reportData.title?.replace(/[^a-zA-Z0-9]/g, '_') || 'rapport';
      const extension = format === 'excel' ? 'xlsx' : format;
      link.download = `${cleanTitle}_${timestamp}.${extension}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error: any) {
      console.error('Erreur lors de l\'export:', error);
      throw error;
    }
  }
}

// Instance singleton du service de statistiques
const statisticsService = new StatisticsService();
export default statisticsService;